"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { isValidElement, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { cx } from "../internal/cx.js";
import { plainClick } from "../internal/navigate.js";
import { IconMenu, IconX } from "../internal/icons.js";
import { useEscapeLayer } from "../internal/layers.js";
import { isBackdropClick, useModalDialog } from "../internal/useDialog.js";
import { IconButton } from "./Button.js";
import { Logo } from "./Logo.js";
import { Menu } from "./Menu.js";
import { PageHeader } from "./Panel.js";
import { NotificationBell } from "./Notifications.js";
import { NotificationsProvider } from "./Toast.js";
import { Counter } from "./Badge.js";
/** From this width of the frame's own room the sections are a column on the left, one click away (facts model shell_layout, П339). */
export const RAIL_FROM = 1200;
/** Before paint in the browser; on the server (no layout) — after, so a server-rendered host does not warn. */
const useBeforePaint = typeof window === "undefined" ? useEffect : useLayoutEffect;
/**
 * Whether the frame itself is wide (facts room_layout, П351): its own width decides, not the window's — the window may be
 * wide while the frame is given little room (a bug-report panel on the right, a split screen). The server and the first
 * client render see «narrow» (hydration matches); then the frame is measured before paint and on every change of its width.
 */
function useWideRoom(ref) {
    const [wide, setWide] = useState(false);
    useBeforePaint(() => {
        const node = ref.current;
        if (!node)
            return undefined;
        const sync = (width) => { if (width > 0)
            setWide(width >= RAIL_FROM); };
        sync(node.getBoundingClientRect().width);
        if (typeof ResizeObserver === "undefined")
            return undefined;
        const observer = new ResizeObserver(entries => sync(entries[0]?.contentRect.width ?? 0));
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref]);
    return wide;
}
/** The collapsed column, remembered per product in this browser. Read after mount only (the server and the first client
 *  render see the expanded column — hydration matches); storage blocked or full — the choice lives until a reload. */
function useCollapsed(key) {
    const [collapsed, setCollapsed] = useState(false);
    useEffect(() => {
        try {
            setCollapsed(window.localStorage.getItem(key) === "1");
        }
        catch { /* storage blocked: expanded */ }
    }, [key]);
    const toggle = () => setCollapsed(value => {
        try {
            window.localStorage.setItem(key, value ? "0" : "1");
        }
        catch { /* storage blocked: this session only */ }
        return !value;
    });
    return [collapsed, toggle];
}
const isAccount = (value) => !!value && typeof value === "object" && !isValidElement(value) && typeof value.name === "string" && Array.isArray(value.items);
/**
 * Fallback marks of sections without an icon in the collapsed column (rail_marks Рм2): the first letter, two letters when
 * another icon-less section starts with the same letter, the letter and its number when even two letters collide.
 */
export function railMarks(nav) {
    const bare = nav.map((item, index) => ({ index, label: item.label.trim(), bare: item.icon == null }));
    const take = (label, n) => label.slice(0, n).replace(/^./, c => c.toUpperCase());
    const marks = bare.map(item => take(item.label, 1));
    for (const item of bare) {
        if (!item.bare)
            continue;
        const same = bare.filter(other => other.bare && take(other.label, 1) === marks[item.index]);
        if (same.length > 1)
            marks[item.index] = take(item.label, 2);
    }
    const twoLetters = [...marks];
    for (const item of bare) {
        if (!item.bare)
            continue;
        const same = bare.filter(other => other.bare && twoLetters[other.index] === twoLetters[item.index]);
        if (same.length > 1)
            marks[item.index] = `${take(item.label, 1)}${same.findIndex(other => other.index === item.index) + 1}`;
    }
    return marks;
}
function NavList({ nav, current, product, onPick, iconsOnly = false }) {
    const marks = railMarks(nav);
    return _jsx("nav", { "aria-label": product, children: _jsx("ul", { className: "jui-nav-list", children: nav.map((item, index) => _jsxs("li", { children: [item.group && item.group !== nav[index - 1]?.group && _jsx("div", { className: "jui-nav-group", children: item.group }), (() => {
                        const on = item.key === current;
                        const inner = _jsxs(_Fragment, { children: [item.icon ?? (iconsOnly && _jsx("span", { className: "jui-nav-mono", "aria-hidden": "true", children: marks[index] })), _jsx("span", { className: "jui-nav-label", children: item.label }), !!item.count && _jsx("span", { className: "jui-nav-count", children: item.count }), !!item.alert && _jsx(Counter, { value: item.alert, tone: item.alertTone ?? "error", strong: true, label: `${item.label}: требуют внимания ${item.alert}` })] });
                        return item.href
                            ? _jsx("a", { href: item.href, className: cx("jui-nav-item", on && "jui-nav-item--on"), "aria-current": on ? "page" : undefined, title: iconsOnly ? item.label : undefined, onClick: event => { if (plainClick(event)) {
                                    event.preventDefault();
                                    onPick(item.key);
                                } }, children: inner })
                            : _jsx("button", { type: "button", className: cx("jui-nav-item", on && "jui-nav-item--on"), "aria-current": on ? "page" : undefined, title: iconsOnly ? item.label : undefined, onClick: () => onPick(item.key), children: inner });
                    })()] }, item.key)) }) });
}
function AccountButton({ account }) {
    const [anchor, setAnchor] = useState(null);
    const initial = account.name.trim().charAt(0).toUpperCase() || "?";
    return _jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "jui-account", "aria-haspopup": "menu", "aria-expanded": !!anchor, "aria-label": `Учётная запись: ${account.name}`, onClick: event => setAnchor(anchor ? null : event.currentTarget), children: [_jsx("span", { className: "jui-account-avatar", "aria-hidden": "true", children: initial }), _jsxs("span", { className: "jui-account-text", children: [_jsx("span", { ...account.nameProps, className: cx("jui-account-name", account.nameProps?.className), children: account.name }), account.detail && _jsx("span", { className: "jui-account-detail", children: account.detail })] })] }), _jsx(Menu, { anchor: anchor, label: account.name, onClose: () => setAnchor(null), items: account.items })] });
}
/**
 * The standard frame of Jaiora internal screens: a bar with ☰, the Jaiora mark and the product name; on the right the bell
 * of the notification centre and the account button. ☰ is always there (CRM ui_screens Бг1–Бг4). From 1200 px of the frame's own room
 * (room_layout П351 — the frame's width, not the window's) the sections are a column on the left (П339) and ☰ collapses it to icons with tooltips and back — the section stays one click away,
 * the content takes the room, the choice is remembered per product in this browser (`railKey`). Narrower — a side menu by
 * ☰: Esc, a click outside or a chosen section close it and return focus to ☰ (П313); the frame growing past 1200 px
 * closes it with focus on ☰.
 */
export function AppShell({ product, nav, current, onNavigate, account, notifications = true, theme = "light", homeHref, onHome, railKey, children, className }) {
    const [open, setOpen] = useState(false);
    const shell = useRef(null);
    const wide = useWideRoom(shell);
    const [collapsed, toggleRail] = useCollapsed(railKey ?? `jui-rail-collapsed:${product}`);
    const railId = useId();
    const burger = useRef(null);
    const ref = useModalDialog(open && !wide);
    const close = () => { setOpen(false); burger.current?.focus(); };
    useEscapeLayer(open && !wide, close);
    useEffect(() => { if (wide && open)
        close(); }, [wide]);
    const iconsOnly = wide && collapsed;
    const brand = _jsxs(_Fragment, { children: [_jsx(Logo, {}), _jsx("span", { className: "jui-appbar-name", children: product }), _jsx("span", { className: "jui-appbar-kicker", children: "Jaiora" })] });
    const frame = _jsxs("div", { ref: shell, className: cx("jui-app-shell", wide && "jui-app-shell--rail", iconsOnly && "jui-app-shell--collapsed", className), "data-theme": theme, children: [_jsxs("header", { className: "jui-appbar", children: [wide
                        ? _jsx(IconButton, { ref: burger, label: "\u0420\u0430\u0437\u0434\u0435\u043B\u044B", icon: _jsx(IconMenu, {}), "aria-expanded": !collapsed, "aria-controls": railId, onClick: toggleRail })
                        : _jsx(IconButton, { ref: burger, label: "\u0420\u0430\u0437\u0434\u0435\u043B\u044B", icon: _jsx(IconMenu, {}), "aria-expanded": open, "aria-haspopup": "dialog", onClick: () => setOpen(true) }), homeHref ? _jsx("a", { className: "jui-appbar-brand", href: homeHref, onClick: onHome && (event => { if (plainClick(event)) {
                            event.preventDefault();
                            onHome();
                        } }), children: brand }) : _jsx("span", { className: "jui-appbar-brand", children: brand }), _jsxs("div", { className: "jui-appbar-end", children: [notifications && _jsx(NotificationBell, {}), isAccount(account) ? _jsx(AccountButton, { account: account }) : account] })] }), wide && _jsx("aside", { id: railId, className: "jui-app-rail", children: _jsx(NavList, { nav: nav, current: current, product: product, onPick: onNavigate, iconsOnly: iconsOnly }) }), !wide && _jsx("dialog", { ref: ref, className: "jui-nav-drawer", "aria-label": "\u0420\u0430\u0437\u0434\u0435\u043B\u044B", onCancel: event => { event.preventDefault(); close(); }, onClick: event => { if (isBackdropClick(event))
                    close(); }, children: open && _jsxs(_Fragment, { children: [_jsxs("div", { className: "jui-nav-head", children: [_jsx("span", { className: "jui-appbar-brand", children: brand }), _jsx(IconButton, { label: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", icon: _jsx(IconX, {}), onClick: close })] }), _jsx(NavList, { nav: nav, current: current, product: product, onPick: key => { close(); onNavigate(key); } })] }) }), _jsx("main", { className: "jui-app-main", children: children })] });
    return notifications ? _jsx(NotificationsProvider, { children: frame }) : frame;
}
/** The page inside AppShell: one gutter (a token, the same next to the expanded and the collapsed rail) and the rest of the
 *  window for the content. Put a PageHeader first. */
export function Page({ width = "fluid", children, className, ...rest }) {
    const reading = width === "reading" || width === "narrow";
    return _jsx("div", { ...rest, className: cx("jui-page", reading && "jui-page--reading", className), children: children });
}
/** The standard list page (docs/standard.md, § Рамка): the title with its count and main action, filters, the list. */
export function ListPage({ title, count, actions, filters, children, className }) {
    return _jsxs(Page, { className: cx("jui-list-page", className), children: [_jsx(PageHeader, { title: title, count: count, actions: actions }), filters && _jsx("div", { className: "jui-list-page-filters", role: "search", children: filters }), children] });
}
